<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { chevronBackOutline, downloadOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import TicketDetailItem from '@/shared/components/tickets/TicketDetailItem.vue';
import QueueTicketItem from '@/modules/queue/components/QueueTicketItem.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useEvents from '@/modules/events/composables/useEvents';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useUtils from '@/shared/composables/useUtils';
import type { EventTicket } from '@/shared/interfaces/event';
import type { QueueSlot } from '@/modules/queue/composables/useQueue';
import { Event } from '@/shared/interfaces/event';

// Init
const { t } = useI18n();
const route = useRoute();
const eventId = +route.params.eventId;
const ticketIdToScroll = route.params.ticketId.toString();
const { downloadTicket, getEventTickets, updateTicket } = useEvents();
const { eventTickets, isFetchingEventTickets, refetchTickets } = getEventTickets(eventId);
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const { dragToScroll } = useUtils();
const downloadingTicket = ref({ ticketIndex: -1, isDownloading: false });

// onMountedHook
onMounted(async () => {
	// Update ticket if query params are present
	const shouldRefetchTickets = await updateTicket();
	if (shouldRefetchTickets) {
		refetchTickets();
	}

	// Scroll to ticket id
	setTimeout(() => {
		const ticketElement = document.getElementById(`ticket-${ticketIdToScroll}`);
		if (ticketElement) {
			ticketElement.scrollIntoView({
				behavior: 'smooth',
				inline: 'center',
			});
		} else {
			// Try to find queue ticket by checking parent containers
			const queueTickets = document.querySelectorAll('.queue-ticket-item');
			queueTickets.forEach((el) => {
				const slotId = (el as HTMLElement).dataset?.slotId;
				if (slotId && eventTickets.value?.some((t) => t.queue_slot?.id?.toString() === slotId && t.id.toString() === ticketIdToScroll)) {
					el.scrollIntoView({
						behavior: 'smooth',
						inline: 'center',
					});
				}
			});
		}
	}, 500);

	// Make content draggable to scroll on desktop
	if (!Capacitor.isNativePlatform()) {
		dragToScroll('dragToScrollElement');
	}
});

// Refetch tickets
const onRefetchTickets = () => {
	refetchTickets();
};

const router = useRouter();

// Check if ticket is a queue ticket
const isQueueTicket = (ticket: EventTicket) => {
	return !!ticket.queue_activity_id;
};

// Check if ticket has a booked slot
const hasBookedSlot = (ticket: EventTicket) => {
	return !!ticket.queue_slot;
};

// Convert event ticket to queue slot format for QueueTicketItem
const convertToQueueSlot = (ticket: EventTicket): QueueSlot | null => {
	if (!ticket.queue_slot) return null;

	// The queue_slot from API should already be in QueueSlot format
	// but we need to ensure it has all required fields
	return {
		...ticket.queue_slot,
		queue_activity: ticket.queue_activity || ticket.queue_slot.queue_activity,
		user:
			ticket.queue_slot.user || ticket.user
				? {
						id: ticket.user?.id || ticket.user_id,
						full_name: ticket.user?.full_name || ticket.participant?.full_name || '',
						email: ticket.user?.email || ticket.participant?.email || '',
					}
				: {
						id: ticket.user_id,
						full_name: '',
						email: '',
					},
	} as QueueSlot;
};

// Handle buy fastpass (for queue tickets)
const buyFastpass = (slotId: number) => {
	router.push(`/queue/upsell/${slotId}`);
};

// Handle book now for unbooked tickets
const handleBookNow = (ticket: EventTicket) => {
	if (!ticket.queue_activity_id) return;

	// Store event ticket ID(s) in sessionStorage
	const ticketIds = [ticket.id];
	sessionStorage.setItem('booking_event_ticket_ids', JSON.stringify(ticketIds));

	// Navigate to my-queues page
	router.push('/my-queues');
};

// Start downloading ticket
const startDownloadingTicket = async (event: Event, ticket: EventTicket, idx: number) => {
	downloadingTicket.value = { ticketIndex: idx, isDownloading: true };
	await downloadTicket(event, ticket);
	downloadingTicket.value.isDownloading = false;
};
</script>

<template>
	<ion-content>
		<div class="flex flex-col">
			<TopBar class="px-[34px] mb-4">
				<template #left>
					<ion-button class="w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full" shape="round">
						<router-link :to="`/events/${eventId}/tickets`" class="flex items-center justify-center">
							<ion-icon :icon="chevronBackOutline" class="ion-no-padding ion-no-margin text-black mr-[2px]"></ion-icon>
						</router-link>
					</ion-button>
				</template>

				<template #center>
					<div class="flex grow items-center justify-center w-full">
						<div class="mr-2">
							<span class="font-sourcesanspro-semibold text-l2">{{ t('events.tickets.tickets_detail.title') }}</span>
						</div>
						<!-- <div>
              <span class="font-sourcesanspro-regular text-base font-color-text-50">(7/24)</span>
            </div> -->
					</div>
				</template>
			</TopBar>
		</div>
		<div
			id="dragToScrollElement"
			class="absolute top-[132px] left-0 flex w-[100%] max-w-[100%] invisible-scroller overflow-x-scroll"
			:class="[
				{
					'justify-center': eventTickets && eventTickets.length === 1,
				},
			]">
			<div
				v-for="(ticket, idx) in eventTickets"
				:key="idx"
				class="flex flex-col"
				:class="[
					{
						'items-start justify-between': eventTickets && eventTickets?.length > 1,
						'items-center': eventTickets && eventTickets.length === 1,
					},
				]">
				<!-- Queue Ticket with booked slot -->
				<div
					v-if="isQueueTicket(ticket) && hasBookedSlot(ticket) && convertToQueueSlot(ticket)"
					class="flex flex-col"
					:class="[
						{
							'ml-[34px] mr-4': eventTickets && eventTickets?.length > 1 && idx === 0,
							'mr-8': eventTickets && eventTickets?.length > 1 && idx === eventTickets!.length - 1,
							'mr-4': eventTickets && eventTickets?.length > 1 && idx !== eventTickets!.length - 1,
						},
					]">
					<!-- Download Button -->
					<div class="flex items-center justify-center mb-2">
						<ion-button v-if="!ticket.event?.delayed_delivery_datetime || (ticket.event?.delayed_delivery_datetime && eventTickets && eventTickets[0].qr_code)" class="hover:bg-white hover:rounded-full" size="large" shape="round" @click="startDownloadingTicket(ticket.event, ticket, idx)">
							<div class="flex items-center justify-center">
								<ion-spinner v-if="downloadingTicket.ticketIndex === idx && downloadingTicket.isDownloading" name="dots" class="text-dark-blue mr-2"></ion-spinner>
								<ion-icon v-else :icon="downloadOutline" class="text-dark-blue mr-2"></ion-icon>
								<span class="text-m2">
									{{ downloadingTicket.ticketIndex === idx && downloadingTicket.isDownloading ? t('events.tickets.tickets_detail.downloading') : t('events.tickets.tickets_detail.download') }}
								</span>
							</div>
						</ion-button>
					</div>
					<div :id="`ticket-${ticket.id}`" class="mb-6">
						<QueueTicketItem :slot="convertToQueueSlot(ticket)!" :wallet-linked="!!ticket.wallet_id" :hide-qr-code="ticket.user_id !== userProfile?.id" @buy-fastpass="buyFastpass" />
					</div>
				</div>

				<!-- Regular Event Ticket or Unbooked Queue Ticket -->
				<TicketDetailItem
					v-else
					@on-refetch-tickets="onRefetchTickets"
					:key="idx"
					:qr-code="ticket.qr_code"
					:hide-qr-code="ticket.user_id !== userProfile?.id"
					:idx="idx"
					:value-only="!!ticket.ticket_type?.value_only"
					:ticket="ticket"
					:ticket-id="ticket.id.toString()"
					:event-tickets="eventTickets"
					:name="ticket.participant?.full_name ? ticket.participant?.full_name : userProfile?.full_name ? userProfile?.full_name : ''"
					:email="ticket.participant?.full_name ? ticket.participant?.email : userProfile?.full_name ? userProfile?.email : ''"
					:ticket-disabled-edit="ticket.disable_edit"
					:ticket-type-color="ticket.ticket_type?.color ? ticket.ticket_type?.color : undefined"
					:ticket-type-name="ticket.combo_ticket_type ? ticket.combo_ticket_type.name : ticket.ticket_type?.name ? ticket.ticket_type?.name : ''"
					:ticket-type-description="ticket.combo_ticket_type ? ticket.combo_ticket_type.description : ticket.ticket_type?.description ? ticket.ticket_type?.description : ''"
					:event-allow-edit-tickets="!!ticket.event.allow_edit_tickets"
					:event-id="ticket.event_id"
					:event-name="ticket.event.name"
					:event-date="ticket.ticket_type?.custom_datetime ? ticket.ticket_type.custom_datetime : ticket.event.start_datetime"
					:venue="ticket.event.address"
					:wallet-linked="ticket.wallet_id"
					:class="[
						{
							'ml-[34px] mr-4': eventTickets && eventTickets?.length > 1 && idx === 0,
							'mr-8': eventTickets && eventTickets?.length > 1 && idx === eventTickets!.length - 1,
							'mr-4': eventTickets && eventTickets?.length > 1 && idx !== eventTickets!.length - 1,
						},
					]"
					class="mb-6" />

				<div
					class="flex w-[300px] items-center justify-center"
					:class="[
						{
							'ml-[34px] mr-4': eventTickets && eventTickets?.length > 1 && idx === 0,
							'mr-4': eventTickets && eventTickets?.length > 1 && idx > 0,
						},
					]">
					<!-- <ion-button
			class="w-full"
			expand="full"
			size="large"
			shape="round"
			fill="outline">
			<div class="flex items-center justify-center text-white">
				<ion-icon :icon="shareSocialOutline" class="mr-2"></ion-icon>
				<span class="text-m2">Share</span>
			</div>
			</ion-button> -->
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
